import { useMemo, useCallback } from 'react'
import { QueryMode, ChartDataPoint, ChartSeries, ResultsData } from '../types'
import { useContextState } from '../../../../../../../hooks/theme'
import { getChartColorByIndex } from '../../../../../../../utils/methods/chartColors'

interface UseChartDataProps {
  resultsData: ResultsData
  selectedResultIds: string[]
  queryMode: QueryMode
}

interface UseChartDataReturn {
  chartData: ChartDataPoint[]
  chartSeries: ChartSeries[]
  chartColors: (index: number) => string
}

const useChartData = ({
  resultsData,
  selectedResultIds,
  queryMode,
}: UseChartDataProps): UseChartDataReturn => {
  const { colors } = useContextState()

  const { chartData, chartSeries } = useMemo(() => {
    if (!resultsData || typeof resultsData !== 'object' || Array.isArray(resultsData)) {
      return { chartData: [], chartSeries: [] }
    }
    const rawData = resultsData as ResultsData
    const allWeeks = Object.keys(rawData).sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ''), 10)
      const numB = parseInt(b.replace(/\D/g, ''), 10)
      return numA - numB
    })

    const isStoreMode = queryMode === 'many-units-one-questionnaire'

    const seriesMap = new Map<string, ChartSeries>()
    for (const week of allWeeks) {
      for (const record of rawData[week] ?? []) {
        const id = isStoreMode ? String(record.storeId) : String(record.questionnaireId)
        const name = isStoreMode ? record.storeName : record.questionnaireName
        if (selectedResultIds.includes(id) && !seriesMap.has(id) && name) {
          seriesMap.set(id, { id, name })
        }
      }
    }

    const series = Array.from(seriesMap.values())
    const data: ChartDataPoint[] = allWeeks.map((week) => {
      const point: ChartDataPoint = { week }
      const weekRecords = rawData[week] ?? []
      for (const s of series) {
        const record = weekRecords.find((r) =>
          isStoreMode ? String(r.storeId) === s.id : String(r.questionnaireId) === s.id
        )
        point[s.id] = record?.score ?? null
      }
      return point
    })

    return { chartData: data, chartSeries: series }
  }, [resultsData, selectedResultIds, queryMode])

  const chartColors = useCallback((index: number) => {
    return getChartColorByIndex(colors, index)
  }, [colors])

  return {
    chartData,
    chartSeries,
    chartColors,
  }
}

export { useChartData }
